import React, { useState } from 'react';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { Box, Popover, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useTranslatedMessages } from '../../../../hooks/useTranslatedMessages';
import { getDatePickerProviderProps } from '../../../../utils/datePickerLocale';
import { TIME_PERIOD_PRESETS, TIME_PERIOD_PRESET_ORDER } from '../../constants/documentReportFilterConstants';
import {
  computeTimePeriodDates,
  formatTimePeriodDisplayLabel,
  getTimePeriodPresetLabel,
} from '../../utils/timePeriodUtils';
import FloatingFieldShell from './FloatingFieldShell';
import PeriodRangeCalendar from './PeriodRangeCalendar';

const ReportTimePeriodFilter = ({ value, onChange, required = false, error = false, width = 180, id }) => {
  const { language, documentReports } = useTranslatedMessages();
  const labels = documentReports?.filters?.time_period ?? {};
  const datePickerLocale = getDatePickerProviderProps(language);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const { preset, startDate, endDate } = value ?? {};
  const displayLabel = formatTimePeriodDisplayLabel({ preset, startDate, endDate, labels });

  const handlePresetSelect = (nextPreset) => {
    onChange({ preset: nextPreset, ...computeTimePeriodDates(nextPreset) });
    setAnchorEl(null);
  };

  const handleRangeChange = ({ startDate: nextStart, endDate: nextEnd }) => {
    const today = dayjs().endOf('day');
    const cappedStart = nextStart && nextStart.isAfter(today) ? today.startOf('day') : nextStart;
    const cappedEnd = nextEnd && nextEnd.isAfter(today) ? today : nextEnd;

    onChange({ preset: TIME_PERIOD_PRESETS.CUSTOM, startDate: cappedStart, endDate: cappedEnd });
  };

  const handleClear = () => {
    onChange({ preset: TIME_PERIOD_PRESETS.CUSTOM, startDate: null, endDate: null });
    setAnchorEl(null);
  };

  return (
    <FloatingFieldShell
      label={labels.label ?? 'Time Period'}
      required={required}
      error={error}
      active={open}
      floated={open || Boolean(displayLabel)}
      sx={{ width, minWidth: width, flexShrink: 0 }}
    >
      <Box
        id={id}
        role="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        tabIndex={0}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setAnchorEl(event.currentTarget);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          width: '100%',
          height: '100%',
          px: 1.5,
          cursor: 'pointer',
        }}
      >
        <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
        <Typography variant="body2" noWrap sx={{ flex: 1, fontSize: 12 }}>
          {displayLabel}
        </Typography>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, borderRadius: 1.5, overflow: 'hidden' } } }}
      >
        <Stack direction="row">
          <Box
            sx={{
              width: 150,
              flexShrink: 0,
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: (theme) => alpha(theme.palette.background.paper, 0.5),
              py: 1,
            }}
          >
            {TIME_PERIOD_PRESET_ORDER.map((presetOption) => {
              const isActive = preset === presetOption;

              return (
                <Box
                  key={presetOption}
                  component="button"
                  type="button"
                  onClick={() => handlePresetSelect(presetOption)}
                  sx={{
                    width: '100%',
                    textAlign: 'left',
                    border: 'none',
                    cursor: 'pointer',
                    px: 2,
                    py: 1,
                    fontSize: 13,
                    fontFamily: 'inherit',
                    color: isActive ? 'primary.main' : 'text.primary',
                    fontWeight: isActive ? 600 : 400,
                    bgcolor: isActive ? 'background.paper' : 'transparent',
                    transition: 'background-color 150ms ease',
                    '&:hover': { bgcolor: 'background.paper' },
                  }}
                >
                  {getTimePeriodPresetLabel(presetOption, labels)}
                </Box>
              );
            })}
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs} {...datePickerLocale}>
            <PeriodRangeCalendar
              startDate={startDate}
              endDate={endDate}
              onChange={handleRangeChange}
              onClear={handleClear}
              maxDate={dayjs()}
              labels={labels}
            />
          </LocalizationProvider>
        </Stack>
      </Popover>
    </FloatingFieldShell>
  );
};

export default ReportTimePeriodFilter;
